import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Wallet, Copy, Check, Upload, ArrowDownLeft, Clock, CheckCircle, XCircle, TrendingUp, TrendingDown, Calculator, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getWalletAddresses, createDeposit, getDeposits, uploadProof, getCryptoPrices, getPlatformSettings } from '../services/api';
import { toast } from 'sonner';

const DepositPage = () => {
  const { user } = useAuth();
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [wallets, setWallets] = useState({});
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cryptoPrices, setCryptoPrices] = useState({});
  const [settings, setSettings] = useState({});

  const cryptoOptions = [
    { id: 'BTC', name: 'Bitcoin', icon: '₿', color: 'from-orange-500 to-yellow-500' },
    { id: 'ETH', name: 'Ethereum', icon: 'Ξ', color: 'from-purple-500 to-blue-500' },
    { id: 'USDT', name: 'Tether', icon: '₮', color: 'from-emerald-500 to-green-500' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, depositsRes, settingsRes] = await Promise.all([
        getWalletAddresses(),
        getDeposits(),
        getPlatformSettings()
      ]);
      setWallets(walletsRes.data);
      setDeposits(depositsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
    // Fetch prices separately (non-blocking)
    try {
      const pricesRes = await getCryptoPrices();
      setCryptoPrices(pricesRes.data);
    } catch { /* silent fail for prices */ }
  };

  const handleCopy = () => {
    const address = wallets[selectedCrypto.toLowerCase()];
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success('Address copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const response = await uploadProof(file);
        setProofFile(response.data);
        toast.success('Proof uploaded successfully');
      } catch (error) {
        toast.error('Failed to upload proof');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      await createDeposit({
        amount: parseFloat(amount),
        crypto_type: selectedCrypto,
        proof_image: proofFile?.filename
      });
      toast.success('Deposit request submitted! Awaiting confirmation.');
      setAmount('');
      setProofFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deposit');
    } finally {
      setLoading(false);
    }
  };

  const depositFeePercent = parseFloat(settings.deposit_fee || 0);
  const depositAmount = parseFloat(amount) || 0;
  const feeAmount = depositAmount * (depositFeePercent / 100);
  const netAmount = depositAmount - feeAmount;
  const cryptoPrice = cryptoPrices[selectedCrypto]?.price || 0;
  const cryptoEquivalent = cryptoPrice > 0 ? netAmount / cryptoPrice : 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="badge badge-success"><CheckCircle className="w-3 h-3 mr-1" />Approved</span>;
      case 'rejected':
        return <span className="badge badge-danger"><XCircle className="w-3 h-3 mr-1" />Rejected</span>;
      default:
        return <span className="badge badge-warning"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen">
      <div className="animated-bg" />
      <div className="noise-overlay" />
      <Navbar />

      <main className="pt-32 pb-20 px-6" data-testid="deposit-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <h1 className="text-4xl font-bold font-['Outfit'] text-white mb-4">Deposit Funds</h1>
            <p className="text-slate-400">Add funds to your account using cryptocurrency</p>
          </motion.div>

          {/* Live Crypto Prices Banner */}
          {Object.keys(cryptoPrices).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="flex gap-3 mb-8 overflow-x-auto pb-1 scrollbar-hide" data-testid="crypto-prices"
            >
              {cryptoOptions.map((c) => {
                const p = cryptoPrices[c.id];
                if (!p) return null;
                const isUp = p.change_24h >= 0;
                return (
                  <div key={c.id} className="flex-1 min-w-[140px] rounded-xl border border-white/[0.06] bg-slate-900/40 p-3 sm:p-4">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white font-bold text-xs sm:text-sm">{c.icon}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-semibold leading-tight">{c.id}</p>
                        <p className="text-slate-500 text-[11px] leading-tight">{c.name}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold truncate">${p.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                      <div className={`flex items-center gap-0.5 text-xs mt-0.5 ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isUp ? <TrendingUp className="w-3 h-3 flex-shrink-0" /> : <TrendingDown className="w-3 h-3 flex-shrink-0" />}
                        <span>{Math.abs(p.change_24h).toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Deposit Form */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <ArrowDownLeft className="w-5 h-5 text-emerald-400" />
                Make a Deposit
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Crypto Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">Select Cryptocurrency</label>
                  <div className="grid grid-cols-3 gap-3">
                    {cryptoOptions.map((crypto) => (
                      <button key={crypto.id} type="button" onClick={() => setSelectedCrypto(crypto.id)}
                        className={`p-4 rounded-xl border transition-all ${selectedCrypto === crypto.id ? 'border-cyan-500 bg-cyan-500/10' : 'border-white/10 hover:border-white/20'}`}
                        data-testid={`crypto-${crypto.id.toLowerCase()}`}
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${crypto.color} flex items-center justify-center mx-auto mb-2`}>
                          <span className="text-white font-bold text-lg">{crypto.icon}</span>
                        </div>
                        <p className="text-white text-sm font-medium">{crypto.id}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wallet Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">{selectedCrypto} Wallet Address</label>
                  <div className="bg-black/30 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-white text-sm break-all font-mono flex-1">{wallets[selectedCrypto.toLowerCase()] || 'Loading...'}</p>
                      <button type="button" onClick={handleCopy} data-testid="copy-address-btn"
                        className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-all ${copied ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20'}`}
                      >
                        {copied ? (
                          <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />Copied</span>
                        ) : (
                          <span className="flex items-center gap-1.5"><Copy className="w-4 h-4" />Copy</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-xl">
                    <QRCodeSVG value={wallets[selectedCrypto.toLowerCase()] || ''} size={140} level="H" />
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Deposit Amount (USD)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">$</span>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                      className="input-field" style={{ paddingLeft: '2.5rem' }} placeholder="0.00" min="10" step="0.01" required data-testid="deposit-amount"
                    />
                  </div>
                </div>

                {/* Fee Calculator */}
                {depositAmount > 0 && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} 
                    className="rounded-xl border border-white/[0.06] bg-slate-900/40 p-4 space-y-2" data-testid="fee-calculator"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-semibold text-white">Fee Breakdown</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Deposit Amount</span>
                      <span className="text-white">${depositAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Fee ({depositFeePercent}%)</span>
                      <span className="text-orange-400">-${feeAmount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-white/[0.06] pt-2 flex justify-between text-sm">
                      <span className="text-slate-300 font-medium">Net Credit</span>
                      <span className="text-emerald-400 font-bold">${netAmount.toFixed(2)}</span>
                    </div>
                    {cryptoPrice > 0 && (
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-slate-500 text-xs">Approx. {selectedCrypto}</span>
                        <span className="text-slate-400 text-xs">{cryptoEquivalent.toFixed(selectedCrypto === 'BTC' ? 6 : 4)} {selectedCrypto}</span>
                      </div>
                    )}
                    {netAmount > 0 && (
                      <div className="flex justify-between text-sm pt-1 border-t border-white/[0.04] mt-1">
                        <span className="text-slate-500 text-xs">Est. daily return ({settings.daily_profit_percentage || 3}%)</span>
                        <span className="text-emerald-400 text-xs font-semibold">+${(netAmount * (parseFloat(settings.daily_profit_percentage || 3) / 100)).toFixed(2)}/day</span>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Proof Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Payment Proof (Optional)</label>
                  <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-cyan-500/50 transition-colors">
                    <input type="file" onChange={handleFileChange} accept="image/*" className="hidden" id="proof-upload" />
                    <label htmlFor="proof-upload" className="cursor-pointer">
                      {uploading ? (
                        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      ) : proofFile ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-400">
                          <Check className="w-5 h-5" /><span>Proof uploaded</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-400 text-sm">Click to upload screenshot</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="deposit-submit">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Wallet className="w-4 h-4" />Submit Deposit</>
                  )}
                </button>
              </form>

              <div className="mt-6 space-y-3">
                <div className="p-4 bg-cyan-500/5 rounded-xl border border-cyan-500/15">
                  <p className="text-cyan-400 text-sm">
                    <strong>Instructions:</strong> Send the exact amount to the wallet address above, then click submit. Your deposit will be confirmed within 10-30 minutes.
                  </p>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <p className="text-xs text-emerald-400/80">Your funds are securely processed and begin generating returns automatically</p>
                </div>
              </div>
            </motion.div>

            {/* Deposit History */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Deposit History
              </h2>

              <div className="space-y-4">
                {deposits.length > 0 ? (
                  deposits.map((deposit) => (
                    <div key={deposit.id} className="p-4 bg-white/5 rounded-xl border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-semibold">${deposit.amount.toLocaleString()}</span>
                        {getStatusBadge(deposit.status)}
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">{deposit.crypto_type}</span>
                        <span className="text-slate-400">{formatDate(deposit.created_at)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No deposits yet</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DepositPage;
